import { ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { AuthenticationService, CareCoordinationService } from '@core/service';
import { MemberAspire } from '@shared/model';
import { DialogService } from 'primeng/dynamicdialog';
import { finalize } from 'rxjs/operators';
import { SubSink } from 'subsink';
import { AspireInfoFormComponent } from '../aspire-info-form/aspire-info-form.component';

@Component({
    selector: 'app-aspire-widget',
    templateUrl: './aspire-widget.component.html',
    styleUrls: ['./aspire-widget.component.scss'],
    standalone: false
})
export class AspireWidgetComponent implements OnInit, OnChanges, OnDestroy {
  @Input() tenantId: string;
  @Input() memberId: string;
  @Input() subscriberNumber: string;

  cols = [];
  aspireInfo: MemberAspire[] = [];
  isLoading: boolean;
  isExternalUser:boolean;
  dateFieldsAspire: Array<string> = [
    'referredDate',
    'enrolledDate',
    'dischargeDate',
    'mostRecentAssessmentDate',
  ];

  private readonly subs = new SubSink();

  constructor(
    private careCoordinationService: CareCoordinationService,
    private authenticationService: AuthenticationService,
    private ref: ChangeDetectorRef,
    private dialogService: DialogService,
  ) {
    this.isExternalUser = this.authenticationService.isExternalUser;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['subscriberNumber'].firstChange) {
      this.retrieveDataFromService();
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  ngOnInit() {
    this.initColumns();
    this.retrieveDataFromService();
  }

  onAddAspire(): void {
    this.dialogService.open(AspireInfoFormComponent, {
      header: 'Add New Aspire Information',
      width: '650px',
      closable: false,
      closeOnEscape: false,
    }).onClose.subscribe(data => {
      if (data) {
        this.addAspireInfo(data);
      }
    });
  }

  onEditAspire(rowData: MemberAspire): void {
    this.dialogService.open(AspireInfoFormComponent, {
      data: rowData,
      header: 'Edit Aspire Information',
      width: '650px',
      closable: false,
      closeOnEscape: false,
    }).onClose.subscribe(data => {
      if (data) {
        this.editAspireInfo(data, rowData.memberAspireId);
      }
    });
  }

  private initColumns() {
    this.cols = [
      { field: 'referred', header: 'Referred', style: 'width: 6rem' },
      { field: 'referredDate', header: 'Date Referred', style: 'width: 7rem' },
      { field: 'enrolled', header: 'Enrolled', style: 'width: 6rem' },
      { field: 'enrolledDate', header: 'Enrollment Date', style: 'width: 7rem' },
      { field: 'status', header: 'Status', style: 'width: 7rem' },
      { field: 'stagingStatus', header: 'Staging Status', style: 'width: 12rem' },
      { field: 'mostRecentAssessmentDate', header: 'Most Recent Assessment Date', style: 'width: 11rem' },
      { field: 'primaryDiagnosis', header: 'Primary Diagnosis', style: 'width: 7rem' },
      { field: 'dischargeDate', header: 'Discharge Date', style: 'width: 7rem' },
      { field: 'dischargeReason', header: 'Discharge Reason', style: 'width: 7rem' },
      { field: 'palliativePerformanceScale', header: 'Palliative Performance Scale (%)', style: 'width: 8rem' },
      { field: 'createdBy', header: 'Created By', style: 'width: 12rem' },
      { field: 'createdOn', header: 'Created On', style: 'width: 12rem' },
      { field: 'modifiedBy', header: 'Modified By', style: 'width: 12rem' },
      { field: 'modifiedOn', header: 'Modified On', style: 'width: 12rem' },
    ];
  }

  private retrieveDataFromService(): void {
    if (!this.isExternalUser) {
      this.isLoading = true;

      this.subs.sink = this.careCoordinationService.getAspireInfo(this.tenantId, this.subscriberNumber)
        .pipe(
          finalize(() => {
            this.isLoading = false;
            this.ref.markForCheck();
          })
        )
        .subscribe((aspire) => {
          this.aspireInfo = this.convertDate(this.dateFieldsAspire, aspire);
        });
    }
  }

  private convertDate(dateFields: string[], data: any[]): any {
    return data.map(element => {
      dateFields.forEach(field => {
        if (element[field]) {
          // TODO hot fix date timezone
          const date = element[field] + (typeof element[field] === 'string' && element[field].indexOf('Z') === -1 ? 'Z' : '');
          element[field] = new Date(date);
        }
      });

      return element;
    });
  }

  private editAspireInfo(data: MemberAspire, memberAspireId: string): void {
    this.careCoordinationService.editAspireInfo(this.tenantId, {
      ...data,
      memberAspireId,
      subscriberId: this.memberId,
      modifiedBy: this.authenticationService.claims.emails[0],
    }).subscribe(aspireInfo => {
      if (aspireInfo) {
        this.aspireInfo.forEach((item, index) => {
          if (item.memberAspireId === aspireInfo.memberAspireId) {
            this.aspireInfo[index] = aspireInfo;
          }
        });
        this.aspireInfo = this.convertDate(this.dateFieldsAspire, this.aspireInfo);
      }
    });
  }

  private addAspireInfo(data: MemberAspire): void {
    this.subs.sink = this.careCoordinationService.addAspireInfo(this.tenantId, {
      ...data,
      subscriberId: this.memberId,
      createdBy: this.authenticationService.claims.emails[0],
    }).subscribe(aspireInfo => {
      if (aspireInfo) {
        this.aspireInfo = this.convertDate(this.dateFieldsAspire, [aspireInfo]).concat(this.aspireInfo);
      }
    });
  }
}
